import { WHITE, colorName, type Color } from '../primitives/color.js'
import { assertNever } from '../result.js'

/**
 * How a game stands. `winner` exists only on the decisive variants, so
 * `status.winner` on a draw is a compile error rather than an `undefined` that
 * quietly renders as "undefined wins".
 */
export type GameStatus =
  | { readonly kind: 'playing'; readonly check: boolean }
  | { readonly kind: 'checkmate'; readonly winner: Color }
  | { readonly kind: 'resignation'; readonly winner: Color }
  | { readonly kind: 'timeout'; readonly winner: Color }
  | { readonly kind: 'stalemate' }
  | { readonly kind: 'insufficientMaterial' }
  /** One side flagged, but the other could never have mated. Drawn, not lost. */
  | { readonly kind: 'timeoutVsInsufficientMaterial' }
  | { readonly kind: 'agreement' }
  | { readonly kind: 'fiftyMoveRule' }
  | { readonly kind: 'seventyFiveMoveRule' }
  | { readonly kind: 'threefoldRepetition' }
  | { readonly kind: 'fivefoldRepetition' }
  /** A cost-motivated cutoff for ai-vs-ai matches — see AI_MATCH_MOVE_CAP_PLIES. */
  | { readonly kind: 'moveLimit' }

export type GameResult = '1-0' | '0-1' | '1/2-1/2' | '*'

export const isGameOver = (status: GameStatus): boolean => status.kind !== 'playing'

export const winnerOf = (status: GameStatus): Color | null => {
  switch (status.kind) {
    case 'checkmate':
    case 'resignation':
    case 'timeout':
      return status.winner
    case 'playing':
    case 'stalemate':
    case 'insufficientMaterial':
    case 'timeoutVsInsufficientMaterial':
    case 'agreement':
    case 'fiftyMoveRule':
    case 'seventyFiveMoveRule':
    case 'threefoldRepetition':
    case 'fivefoldRepetition':
    case 'moveLimit':
      return null
    default:
      return assertNever(status, 'game status')
  }
}

export const resultOf = (status: GameStatus): GameResult => {
  if (status.kind === 'playing') return '*'
  const winner = winnerOf(status)
  if (winner === null) return '1/2-1/2'
  return winner === WHITE ? '1-0' : '0-1'
}

/** Neutral prose. The UI layers its own phrasing on top of the `kind`. */
export const describeStatus = (status: GameStatus): string => {
  switch (status.kind) {
    case 'playing':
      return status.check ? 'in progress, in check' : 'in progress'
    case 'checkmate':
      return `${colorName(status.winner)} wins by checkmate`
    case 'resignation':
      return `${colorName(status.winner)} wins by resignation`
    case 'timeout':
      return `${colorName(status.winner)} wins on time`
    case 'stalemate':
      return 'draw by stalemate'
    case 'insufficientMaterial':
      return 'draw by insufficient material'
    case 'timeoutVsInsufficientMaterial':
      return 'draw — time ran out against insufficient material'
    case 'agreement':
      return 'draw by agreement'
    case 'fiftyMoveRule':
      return 'draw by the fifty-move rule'
    case 'seventyFiveMoveRule':
      return 'draw by the seventy-five-move rule'
    case 'threefoldRepetition':
      return 'draw by threefold repetition'
    case 'fivefoldRepetition':
      return 'draw by fivefold repetition'
    case 'moveLimit':
      return 'draw — reached the move limit'
    default:
      return assertNever(status, 'game status')
  }
}
